import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, User, BookOpen, BarChart3, TrendingUp, Award } from 'lucide-react';
import api from '../utils/api';

const StudentProgressReport = () => {
  const navigate = useNavigate();
  const reportRef = useRef(null);
  const [students, setStudents] = useState([]);
  const [terms, setTerms] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [schoolInfo, setSchoolInfo] = useState(null);

  // Fetch students and terms on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [studentsRes, termsRes, schoolRes] = await Promise.all([
          api.get('/school-admin/students'),
          api.get('/igcse/terms'),
          api.get('/school/info')
        ]);
        setStudents(studentsRes.data);
        setTerms(termsRes.data);
        setSchoolInfo(schoolRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        // Try alternate endpoint for students
        try {
          const studentsRes = await api.get('/admin/students');
          setStudents(studentsRes.data);
        } catch (e) {
          console.error('Error fetching students:', e);
        }
      }
    };
    fetchData();
  }, []);

  const fetchReport = useCallback(async () => {
    if (!selectedStudent || !selectedTerm) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/igcse/reports/student/${selectedStudent}?term=${encodeURIComponent(selectedTerm)}`);
      setReport(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [selectedStudent, selectedTerm]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Get grade color
  const getGradeColor = (grade) => {
    const colors = {
      'A*': 'bg-emerald-100 text-emerald-800 border-emerald-200',
      'A': 'bg-green-100 text-green-800 border-green-200',
      'B': 'bg-blue-100 text-blue-800 border-blue-200',
      'C': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'D': 'bg-orange-100 text-orange-800 border-orange-200',
      'E': 'bg-red-100 text-red-800 border-red-200',
      'U': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[grade] || 'bg-gray-100 text-gray-800';
  };

  // Get AO bar color
  const getAOBarColor = (percentage) => {
    if (percentage >= 80) return 'bg-emerald-500';
    if (percentage >= 60) return 'bg-blue-500';
    if (percentage >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  // Print/Download report
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b print:hidden">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Student Progress Report</h1>
                <p className="text-sm text-gray-500">View individual student performance with AO analytics</p>
              </div>
            </div>
            {report && (
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                data-testid="download-report-btn"
              >
                <Download className="w-5 h-5" />
                Print / Download
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm border mb-6 print:hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Select Student</label>
              <select
                value={selectedStudent}
                onChange={(e) => setSelectedStudent(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                data-testid="student-select"
              >
                <option value="">-- Select a student --</option>
                {students.map((student) => (
                  <option key={student.id} value={student.id}>
                    {student.name} ({student.email})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Select Term</label>
              <select
                value={selectedTerm}
                onChange={(e) => setSelectedTerm(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                data-testid="term-select"
              >
                <option value="">-- Select a term --</option>
                {terms.map((term) => (
                  <option key={term.id} value={term.name}>
                    {term.name}
                  </option>
                ))}
                {/* Allow manual entry if no terms configured */}
                {terms.length === 0 && (
                  <>
                    <option value="Term 1 2024">Term 1 2024</option>
                    <option value="Term 2 2024">Term 2 2024</option>
                    <option value="Term 3 2024">Term 3 2024</option>
                  </>
                )}
              </select>
            </div>
          </div>
        </div>

        {loading && (
          <div className="bg-white rounded-xl p-12 text-center shadow-sm border">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading report...</p>
          </div>
        )}

        {!loading && !report && selectedStudent && selectedTerm && (
          <div className="bg-white rounded-xl p-12 text-center shadow-sm border">
            <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Results Found</h3>
            <p className="text-gray-500">No results recorded for this student in the selected term.</p>
          </div>
        )}

        {!loading && !selectedStudent && (
          <div className="bg-white rounded-xl p-12 text-center shadow-sm border">
            <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Select a Student</h3>
            <p className="text-gray-500">Choose a student and term to view their progress report.</p>
          </div>
        )}

        {/* Report Content */}
        {report && (
          <div ref={reportRef} className="bg-white rounded-xl shadow-sm border print:shadow-none print:border-none">
            {/* Report Header */}
            <div className="border-b p-6 bg-gradient-to-r from-indigo-50 to-purple-50 print:bg-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {(schoolInfo?.logo_url || report.school?.logo_url) && (
                    <img
                      src={schoolInfo?.logo_url || report.school?.logo_url}
                      alt="School Logo"
                      className="w-16 h-16 object-contain"
                    />
                  )}
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {schoolInfo?.name || report.school?.name || 'School Name'}
                    </h2>
                    <p className="text-lg text-indigo-600 font-semibold">
                      IGCSE {report.term} Examinations Report
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Student Name</p>
                  <p className="text-xl font-bold text-gray-900">{report.student?.name}</p>
                </div>
              </div>
            </div>

            {/* AO Summary Cards */}
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-indigo-600" />
                Assessment Objectives Performance
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* AO1 */}
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-blue-800">AO1: Knowledge & Understanding</span>
                    <span className="text-xl font-bold text-blue-600">
                      {report.ao_summary?.ao1?.percentage?.toFixed(1) || 0}%
                    </span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full ${getAOBarColor(report.ao_summary?.ao1?.percentage || 0)}`}
                      style={{ width: `${Math.min(report.ao_summary?.ao1?.percentage || 0, 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    {report.ao_summary?.ao1?.marks?.toFixed(1) || 0} / {report.ao_summary?.ao1?.max?.toFixed(1) || 0} marks
                  </p>
                </div>

                {/* AO2 */}
                <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-green-800">AO2: Application</span>
                    <span className="text-xl font-bold text-green-600">
                      {report.ao_summary?.ao2?.percentage?.toFixed(1) || 0}%
                    </span>
                  </div>
                  <div className="w-full bg-green-200 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full ${getAOBarColor(report.ao_summary?.ao2?.percentage || 0)}`}
                      style={{ width: `${Math.min(report.ao_summary?.ao2?.percentage || 0, 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-green-600 mt-2">
                    {report.ao_summary?.ao2?.marks?.toFixed(1) || 0} / {report.ao_summary?.ao2?.max?.toFixed(1) || 0} marks
                  </p>
                </div>

                {/* AO3 */}
                <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-purple-800">AO3: Analysis & Evaluation</span>
                    <span className="text-xl font-bold text-purple-600">
                      {report.ao_summary?.ao3?.percentage?.toFixed(1) || 0}%
                    </span>
                  </div>
                  <div className="w-full bg-purple-200 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full ${getAOBarColor(report.ao_summary?.ao3?.percentage || 0)}`}
                      style={{ width: `${Math.min(report.ao_summary?.ao3?.percentage || 0, 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-purple-600 mt-2">
                    {report.ao_summary?.ao3?.marks?.toFixed(1) || 0} / {report.ao_summary?.ao3?.max?.toFixed(1) || 0} marks
                  </p>
                </div>
              </div>
            </div>

            {/* Subject Results Table */}
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-indigo-600" />
                Subject-wise Performance
              </h3>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="results-table">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 border-b">#</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 border-b">Subject</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700 border-b">P1</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700 border-b">P2</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700 border-b">P3</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700 border-b">P4</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700 border-b">P5</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700 border-b">P6</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700 border-b bg-blue-50">Weighted %</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700 border-b bg-green-50">Grade</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 border-b">Weightages</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.results?.map((result, index) => {
                      // Build paper scores map
                      const paperScores = {};
                      result.paper_scores?.forEach(ps => {
                        paperScores[ps.paper_number] = ps;
                      });
                      
                      // Build weightages string
                      const weightages = result.papers_config?.map(p => 
                        `${p.paper_number} ${p.weightage}%`
                      ).join(', ') || '';

                      return (
                        <tr key={result.id || index} className="hover:bg-gray-50 border-b">
                          <td className="px-4 py-3 text-gray-600">{index + 1}</td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-gray-900">{result.subject_name}</p>
                              <p className="text-xs text-gray-500">{result.track?.toUpperCase()}</p>
                            </div>
                          </td>
                          {['P1', 'P2', 'P3', 'P4', 'P5', 'P6'].map(pNum => {
                            const score = paperScores[pNum];
                            const config = result.papers_config?.find(p => p.paper_number === pNum);
                            return (
                              <td key={pNum} className="px-4 py-3 text-center">
                                {score ? (
                                  <div>
                                    <p className="text-xs text-gray-400">{score.max_marks}</p>
                                    <p className="font-medium text-gray-900">{score.marks_obtained}</p>
                                  </div>
                                ) : config ? (
                                  <span className="text-gray-300">-</span>
                                ) : (
                                  <span className="text-gray-200">·</span>
                                )}
                              </td>
                            );
                          })}
                          <td className="px-4 py-3 text-center bg-blue-50">
                            <span className="font-bold text-blue-700">
                              {result.weighted_percentage?.toFixed(0)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center bg-green-50">
                            <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold border ${getGradeColor(result.grade)}`}>
                              {result.grade}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500">
                            {weightages}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {report.results?.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No subject results recorded for this term.
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t bg-gray-50 print:bg-white">
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <p className="text-sm text-gray-500 mb-2">Teacher Remarks:</p>
                  <div className="border-b border-gray-300 h-16"></div>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-2">Principal Signature:</p>
                  <div className="border-b border-gray-300 h-16"></div>
                </div>
              </div>
              <div className="mt-4 text-center text-xs text-gray-400">
                Report generated on {new Date().toLocaleDateString()}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print\\:hidden {
            display: none !important;
          }
          #root, .min-h-screen, .max-w-7xl, [ref="reportRef"], [ref="reportRef"] * {
            visibility: visible;
          }
          .bg-white.rounded-xl.shadow-sm.border {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default StudentProgressReport;
